import React, { useEffect, useState } from 'react';
import Head from 'next/head';
import Image from 'next/image';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import PageHeader from 'components/PageHeader';
import SafeHtml from 'components/SafeHtml';
import { getInfrastructure } from 'context/actions/infrastructure';

/**
 * Нэг infrastructure төрлийн дэлгэрэнгүй хуудас.
 *
 * @param {string} type - 'railway' | 'road' | 'power_line' | 'township'
 */
export default function InfrastructureDetailLayout({ type }) {
    const { t } = useTranslation();
    const lang = useSelector((s) => s.system.lang || 'mn');

    const [item, setItem] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            setLoading(true);
            const res = await getInfrastructure(type);
            if (res?.data && res.data.length > 0) {
                setItem(res.data[0]);
            }
            setLoading(false);
        })();
    }, [type]);

    if (loading) {
        return (
            <div className="pt-28 md:pt-36 lg:pt-40 pb-20 animate-pulse">
                <div className="max-w-3xl mx-auto px-4 space-y-4">
                    <div className="h-12 bg-brand-cream-dark w-3/4" />
                    <div className="aspect-[16/9] bg-brand-cream-dark my-6" />
                </div>
            </div>
        );
    }

    if (!item) {
        return (
            <div className="py-32 text-center text-brand-gray">
                {t('common.noData')}
            </div>
        );
    }

    const title = lang === 'en' && item.titleEn ? item.titleEn : item.title;
    const description =
        lang === 'en' && item.descriptionEn ? item.descriptionEn : item.description;
    const content =
        lang === 'en' && item.contentEn ? item.contentEn : item.content;
    const statLabel =
        lang === 'en' && item.statLabelEn ? item.statLabelEn : item.statLabel;
    const imageUrl = item.imageUrl || '/placeholders/project-default.svg';

    return (
        <>
            <Head>
                <title>{`${title} — Mongol Refinery`}</title>
            </Head>

            <PageHeader
                slug="project/infrastructure"
                eyebrow={t('menu.infrastructure')}
                title={title}
                subtitle={description}
            />

            <section className="py-16 lg:py-20 bg-white">
                <div className="max-w-4xl mx-auto px-4 lg:px-8">
                    {/* Stat row */}
                    {item.statValue && (
                        <div className="flex items-baseline gap-3 mb-10 pb-6 border-b border-brand-cream-dark">
                            <span className="text-6xl lg:text-7xl font-black text-brand-yellow-dark">
                                {item.statValue}
                            </span>
                            {statLabel && (
                                <span className="text-lg text-brand-gray font-medium">
                                    {statLabel}
                                </span>
                            )}
                        </div>
                    )}

                    {/* Featured image */}
                    <div className="relative aspect-[16/9] bg-brand-dark mb-10">
                        <Image
                            src={imageUrl}
                            alt={title}
                            fill
                            sizes="(max-width: 1024px) 100vw, 896px"
                            className="object-cover"
                            priority
                        />
                    </div>

                    {/* Content */}
                    {content && (
                        <SafeHtml
                            html={content}
                            className="prose-content max-w-none text-brand-dark leading-relaxed"
                        />
                    )}
                </div>
            </section>
        </>
    );
}